import { useState, useCallback } from "react";
import { useQuery, useMutation } from "react-query";
import api from "../services/api";

interface SubscriptionData {
  success: boolean;
  hasActiveSubscription?: boolean;
  subscription?: Subscription;
  status?: string;
}

interface Subscription {
  id?: string;
  status?: string;
  plan?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

interface CheckoutOptions {
  priceId?: string;
  successUrl?: string;
  cancelUrl?: string;
  [key: string]: unknown;
}

interface PortalOptions {
  returnUrl?: string;
  [key: string]: unknown;
}

interface CheckoutResponse {
  success: boolean;
  url?: string;
  error?: string;
  message?: string;
}

interface UseSubscriptionReturn {
  hasActiveSubscription: boolean;
  subscription: Subscription | undefined;
  status: string | undefined;
  isLoading: boolean;
  error: string | null;
  subscribe: (options?: CheckoutOptions) => void;
  manageSubscription: (options?: PortalOptions) => void;
  checkSubscription: () => void;
  isCreatingCheckout: boolean;
  isCreatingPortal: boolean;
  clearError: () => void;
}

export const useSubscription = (): UseSubscriptionReturn => {
  const [error, setError] = useState<string | null>(null);

  // Query to check subscription status
  const {
    data: subscriptionData,
    isLoading: queryLoading,
    error: queryError,
    refetch: checkSubscription,
  } = useQuery<SubscriptionData, Error>(
    "subscription-status",
    async () => {
      const response = await api.subscription.checkSubscription();
      return response as SubscriptionData;
    },
    {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      onError: (err: Error) => {
        console.error("Error checking subscription:", err);
        setError(err.message || "Failed to check subscription");
      },
      onSuccess: () => {
        setError(null);
      },
    },
  );

  // Mutation to create checkout session
  const createCheckoutMutation = useMutation<
    CheckoutResponse,
    Error,
    CheckoutOptions
  >(
    (options) =>
      api.subscription.createCheckout(options) as Promise<CheckoutResponse>,
    {
      onSuccess: (data) => {
        if (data.success && data.url) {
          // External Stripe URL - must use window.location.href, not React Router
          window.location.href = data.url;
        } else {
          throw new Error(
            data.error || data.message || "Failed to create checkout session",
          );
        }
      },
      onError: (
        err: Error & {
          response?: { data?: { error?: string; message?: string } };
        },
      ) => {
        console.error("Error creating checkout:", err);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Failed to create checkout",
        );
      },
    },
  );

  // Mutation to create customer portal session
  const createPortalMutation = useMutation<
    CheckoutResponse,
    Error,
    PortalOptions
  >(
    (options) =>
      api.subscription.createPortal(options) as Promise<CheckoutResponse>,
    {
      onSuccess: (data) => {
        if (data.success && data.url) {
          // External Stripe URL - must use window.location.href, not React Router
          window.location.href = data.url;
        } else {
          throw new Error(
            data.error || data.message || "Failed to create portal session",
          );
        }
      },
      onError: (
        err: Error & {
          response?: { data?: { error?: string; message?: string } };
        },
      ) => {
        console.error("Error creating portal:", err);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Failed to open customer portal",
        );
      },
    },
  );

  // Convenience functions
  const subscribe = useCallback(
    (options: CheckoutOptions = {}) => {
      setError(null);
      createCheckoutMutation.mutate(options);
    },
    [createCheckoutMutation],
  );

  const manageSubscription = useCallback(
    (options: PortalOptions = {}) => {
      setError(null);
      createPortalMutation.mutate(options);
    },
    [createPortalMutation],
  );

  // Combined loading state
  const combinedLoading =
    queryLoading ||
    createCheckoutMutation.isLoading ||
    createPortalMutation.isLoading;

  // Extract subscription info
  const hasActiveSubscription =
    subscriptionData?.success && subscriptionData?.hasActiveSubscription;
  const subscription = subscriptionData?.subscription;
  const subscriptionStatus = subscriptionData?.status;

  return {
    // Subscription data
    hasActiveSubscription: !!hasActiveSubscription,
    subscription,
    status: subscriptionStatus,

    // Loading and error states
    isLoading: combinedLoading,
    error: error || queryError?.message || null,

    // Actions
    subscribe,
    manageSubscription,
    checkSubscription,

    // Mutation states
    isCreatingCheckout: createCheckoutMutation.isLoading,
    isCreatingPortal: createPortalMutation.isLoading,

    // Utils
    clearError: () => setError(null),
  };
};
